/* eslint-disable lines-between-class-members */
import {
  CategoryGuid,
  DegreeTypeGuid,
  ErrorPriority,
  GlobalContextType,
  ParentCategoryGuid,
} from 'types';
import { LogError } from 'utils/logging/LogError';
import { LogWarning } from 'utils/logging';
import { SITE_TYPE } from 'consts';
import { UpdateUserProfileRequest } from './UpdateUserProfileRequestType';

// Module-level singleton instance
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let instance: any = null;

/**
 * Singleton class to manage user profile information
 * Provides centralized access to user profile data, form values, and taxonomy values
 */
class UserProfileManager {
  private consentListeners: (() => void)[] = [];
  private isConsentListenerAdded = false;
  private pageViewIdTaxonomyMap: Record<
    string,
    GlobalContextType['taxonomyValues']
  > = {};

  constructor() {
    if (typeof window === 'undefined') {
      return;
    }

    const checkInterval = setInterval(() => {
      try {
        const canAttach =
          typeof window !== 'undefined' &&
          window.gtmConsentListeners &&
          Array.isArray(window.gtmConsentListeners);

        if (canAttach) {
          clearInterval(checkInterval);
          this.isConsentListenerAdded = true;

          window.gtmConsentListeners!.push(() => {
            setTimeout(() => {
              this.consentListeners.forEach((listener) => listener());
            }, 2000);
          });
        }
      } catch (error) {
        LogError('Error checking for consent listener', {
          description: error instanceof Error ? error.message : 'Unknown error',
          priority: ErrorPriority.ONE,
        });
      }
    }, 100);

    setTimeout(() => {
      clearInterval(checkInterval);
      if (!this.isConsentListenerAdded) {
        LogWarning('gtmConsentListeners not available after 10 seconds', {
          description: 'Consent listener was not added',
          priority: ErrorPriority.ONE,
        });
      }
    }, 10000);
  }

  setPageViewIdTaxonomyMap(
    globalContext: GlobalContextType,
    resetIfNoneSet: boolean = false
  ): void {
    const { pageViewId } = globalContext.sessionInfo;
    let taxonomyValues: GlobalContextType['taxonomyValues'] | null = null;
    const { siteType } = globalContext.siteMeta;

    if (siteType === SITE_TYPE.UNKNOWN || !pageViewId) {
      return;
    }

    const firstRow = globalContext.clickPortal.firstRow?.filterKeyDerivedValues;

    if (!firstRow) {
      LogError('setPageViewIdTaxonomyMap Failed', {
        description: 'First row not returned',
        priority: ErrorPriority.ONE,
      });
      return;
    }

    // for dynamic pages with results on them
    if (firstRow.hasDerivedValues) {
      taxonomyValues = {
        degrees: [firstRow.degree_level_guid as DegreeTypeGuid],
        parentCategories: [firstRow.parent_category_guid as ParentCategoryGuid],
        categories: [firstRow.category_guid as CategoryGuid],
        programs: [],
      };
    } else {
      taxonomyValues = globalContext.taxonomyValues;
    }

    // for dynamic pages that are loaded client side results are not available on page load
    if (resetIfNoneSet && taxonomyValues) {
      const currentTaxonomyValues = this.pageViewIdTaxonomyMap[pageViewId];
      if (currentTaxonomyValues) {
        const hasTaxonomyValues =
          currentTaxonomyValues.degrees.length > 0 ||
          currentTaxonomyValues.parentCategories.length > 0 ||
          currentTaxonomyValues.categories.length > 0;
        if (!hasTaxonomyValues) {
          delete this.pageViewIdTaxonomyMap[pageViewId];
        }
      }
    }

    if (!this.pageViewIdTaxonomyMap[pageViewId] && taxonomyValues) {
      this.pageViewIdTaxonomyMap[pageViewId] = taxonomyValues;
    }
  }

  getPageViewIdTaxonomyMap(
    pageViewId: string
  ): GlobalContextType['taxonomyValues'] {
    return (
      this.pageViewIdTaxonomyMap[pageViewId] || {
        degrees: [],
        parentCategories: [],
        categories: [],
        programs: [],
      }
    );
  }

  removeConsentListener(listener: () => void): void {
    this.consentListeners = this.consentListeners.filter((l) => l !== listener);
  }

  addConsentListener(listener: () => void): void {
    this.consentListeners.push(listener);
  }

  getConsentListeners(): (() => void)[] {
    return [...this.consentListeners];
  }

  // eslint-disable-next-line class-methods-use-this
  async sendProfileUpdates(payload: UpdateUserProfileRequest): Promise<void> {
    const response = await fetch('/api/triadms/uup', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data.error || `HTTP ${response.status}: ${response.statusText}`
      );
    }
  }

  /**
   * Get the singleton instance
   */
  static getInstance(): UserProfileManager {
    if (!instance) {
      instance = new UserProfileManager();
    }
    return instance as UserProfileManager;
  }

  /**
   * Reset the singleton instance (for testing)
   */
  static reset(): void {
    instance = null;
  }
}

// Export singleton instance getter
export const getUserProfileManager = (): UserProfileManager => {
  return UserProfileManager.getInstance();
};

// Export the class for type references if needed
export default UserProfileManager;
